/**
 * Zustand global store — persisted app state for user profile, chart data, and UI.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import type { User, BirthChart } from '@/lib/api'

// ── Auth slice ───────────────────────────────────────────────────────────────

interface AuthSlice {
  user: User | null
  isAuthenticated: boolean
  isAuthLoading: boolean
  setUser: (user: User | null) => void
  setAuthLoading: (loading: boolean) => void
  logout: () => void
}

// ── Chart slice ──────────────────────────────────────────────────────────────

interface ChartSlice {
  birthChart: BirthChart | null
  setBirthChart: (chart: BirthChart | null) => void
}

// ── UI slice ─────────────────────────────────────────────────────────────────

type Theme = 'dark' | 'light'

interface UISlice {
  sidebarCollapsed: boolean
  mobileSidebarOpen: boolean
  activeSection: string
  theme: Theme
  toggleSidebar: () => void
  setSidebarCollapsed: (collapsed: boolean) => void
  openMobileSidebar: () => void
  closeMobileSidebar: () => void
  setActiveSection: (section: string) => void
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
}

// ── Combined store ────────────────────────────────────────────────────────────

type AppStore = AuthSlice & ChartSlice & UISlice

export const useAppStore = create<AppStore>()(
  persist(
    immer((set) => ({
      // Auth
      user: null,
      isAuthenticated: false,
      isAuthLoading: true,

      setUser: (user) => set((state) => {
        state.user = user
        state.isAuthenticated = user !== null
      }),

      setAuthLoading: (loading) => set((state) => {
        state.isAuthLoading = loading
      }),

      logout: () => set((state) => {
        state.user = null
        state.isAuthenticated = false
        state.birthChart = null
      }),

      // Chart
      birthChart: null,
      setBirthChart: (chart) => set((state) => {
        state.birthChart = chart
      }),

      // UI
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      activeSection: 'dashboard',
      // Default dark; AppLayout will override on first mount if prefers-color-scheme: light
      theme: 'dark' as Theme,

      toggleSidebar: () => set((state) => {
        state.sidebarCollapsed = !state.sidebarCollapsed
      }),

      setSidebarCollapsed: (collapsed) => set((state) => {
        state.sidebarCollapsed = collapsed
      }),

      openMobileSidebar: () => set((state) => {
        state.mobileSidebarOpen = true
      }),

      closeMobileSidebar: () => set((state) => {
        state.mobileSidebarOpen = false
      }),

      setActiveSection: (section) => set((state) => {
        state.activeSection = section
      }),

      setTheme: (theme) => set((state) => {
        state.theme = theme
      }),

      toggleTheme: () => set((state) => {
        state.theme = state.theme === 'dark' ? 'light' : 'dark'
      }),
    })),
    {
      name: 'shubh-time-store',
      storage: createJSONStorage(() => localStorage),
      // Only persist non-sensitive UI state and user profile
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
      }),
    },
  ),
)

// Selector hooks for ergonomic use
export const useUser = () => useAppStore((s) => s.user)
export const useIsAuthenticated = () => useAppStore((s) => s.isAuthenticated)
export const useIsAuthLoading = () => useAppStore((s) => s.isAuthLoading)
export const useSidebarCollapsed = () => useAppStore((s) => s.sidebarCollapsed)
export const useMobileSidebarOpen = () => useAppStore((s) => s.mobileSidebarOpen)
export const useTheme = () => useAppStore((s) => s.theme)
export const useToggleTheme = () => useAppStore((s) => s.toggleTheme)
export const useSetTheme = () => useAppStore((s) => s.setTheme)
